// Pure, stateless helpers shared by every provider. No class / this / no
// provider state — safe to import from any normalize / upstream / seats
// module.

import { currency_symbol } from '@resources'

// ─── Type coercion ───────────────────────────────────────────────────────

export const as_string = (v, fallback = '') =>
  typeof v === 'string' ? v : fallback

// Numeric coercion. Accepts numbers and numeric strings because Kyte
// ships fields like baggageAllowance.quantity as strings ("1", "23"); a
// strict typeof-number gate would silently turn them into the fallback.
export const as_number = (v, fallback = 0) => {
  if (typeof v === 'number') {
    return Number.isNaN(v) ? fallback : v
  }
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v)
    return Number.isNaN(n) ? fallback : n
  }
  return fallback
}

export const as_boolean = (v, fallback = false) =>
  typeof v === 'boolean' ? v : fallback

export const as_array = (v) => (Array.isArray(v) ? [...v] : [])

export const is_non_empty_array = (v) => Array.isArray(v) && v.length > 0

export const is_non_empty_object = (v) =>
  !!v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0

// ─── Money ───────────────────────────────────────────────────────────────

// (68000, 2) → "680.00"   (50, 2) → "0.50"   (1200, 0) → "1200"
export const minor_to_decimal = (amount, decimals) => {
  const decimals_n = as_number(decimals)
  const d = decimals_n > 0 ? decimals_n : 0
  const value = as_number(amount)
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value).toString().padStart(d + 1, '0')
  if (d === 0) {
    return `${sign}${abs}`
  }
  const int_part = abs.slice(0, abs.length - d)
  const frac_part = abs.slice(abs.length - d)
  return `${sign}${int_part}.${frac_part}`
}

// ─── Duration (ISO 8601) ─────────────────────────────────────────────────

export const minutes_to_iso_duration = (mins) => {
  const total = as_number(mins)
  if (total <= 0) {
    return 'PT0M'
  }
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) {
    return `PT${m}M`
  }
  if (m === 0) {
    return `PT${h}H`
  }
  return `PT${h}H${m}M`
}

export const iso_duration_to_minutes = (iso) => {
  if (typeof iso !== 'string' || iso.length === 0) {
    return 0
  }
  const h_match = iso.match(/(\d+)H/)
  const m_match = iso.match(/(\d+)M/)
  const hours = h_match ? parseInt(h_match[1], 10) : 0
  const minutes = m_match ? parseInt(m_match[1], 10) : 0
  return hours * 60 + minutes
}

// ─── Shape builders ──────────────────────────────────────────────────────

export const build_empty_endpoint = () => ({
  iataCode: '',
  terminal: '',
  at: '',
  airportName: '',
  cityCode: '',
  cityName: '',
})

export const build_meta = (provider_version = '') => ({
  normalizedAt: new Date().toISOString(),
  providerVersion: provider_version,
})

export const build_frozen_raw = (raw) => Object.freeze({ ...raw })

/**
 * Fold a canonical dictionaries block into the provider's IATA → name
 * caches that `display_carrier` / `display_airport` read. Cache is
 * purely additive: per-IATA merge preserves existing fields when the
 * incoming entry is empty, so a later partial response can't blank a
 * name a previous call already cached.
 *
 * @param {Provider} provider
 * @param {{ locations?: Object, carriers?: Object }} dict
 */
export const merge_dictionaries = (provider, dict) => {
  if (!provider._carriers) provider._carriers = {}
  if (!provider._airports) provider._airports = {}
  const { carriers = {}, locations = {} } = dict || {}
  provider._carriers = { ...provider._carriers, ...carriers }
  Object.keys(locations).forEach((iata) => {
    const incoming = locations[iata] || {}
    const existing = provider._airports[iata] || {}
    provider._airports[iata] = {
      name: incoming.name || existing.name || '',
      cityCode: incoming.cityCode || existing.cityCode || '',
      cityName: incoming.cityName || existing.cityName || '',
    }
  })
}

export const sum_itinerary_durations = (itineraries) => {
  const list = Array.isArray(itineraries) ? itineraries : []
  const total = list.reduce((acc, itin) => {
    const { duration = '' } = itin || {}
    return acc + iso_duration_to_minutes(duration)
  }, 0)
  return minutes_to_iso_duration(total)
}

/**
 * Attach `currencySymbol` to a price-shaped object so UI templates can
 * read it directly instead of calling currency_symbol at render time.
 * Accepts both `currency` and the legacy `currencyCode` key used on
 * Amadeus bag / seat price sub-objects.
 *
 * @template T
 * @param {T} price
 * @returns {T & { currencySymbol: string }}
 */
export const with_currency_symbol = (price) => {
  if (!price || typeof price !== 'object') {
    return price
  }
  const code = as_string(price.currency || price.currencyCode)
  return { ...price, currencySymbol: currency_symbol(code) }
}

/**
 * Build a canonical Price block from a major-unit decimal string + ISO
 * currency code. Used wherever a sub-price (bag charge, seat charge,
 * additional-service roll-up entry) lacks a separate base/taxes
 * breakdown — total === grandTotal, base === total (so get_markup reads
 * price.base correctly), taxes = []. Markup is applied at render time,
 * never baked here.
 *
 * @param {string} decimal_string
 * @param {string} currency_code
 * @returns {Object}
 */
export const make_price = (decimal_string, currency_code) => ({
  total: as_string(decimal_string),
  grandTotal: as_string(decimal_string),
  base: as_string(decimal_string),
  taxes: [],
  currency: as_string(currency_code),
  currencySymbol: currency_symbol(currency_code),
})

/**
 * Lift `traveler_pricings[0].fareDetailsBySegment[0].amenities` onto
 * the offer level (the Amadeus convention every provider follows).
 * The optional `transform` lets each provider shape entries (Amadeus
 * wraps each via build_amenity; Kyte slices raw).
 *
 * @param {Array} traveler_pricings
 * @param {Function} [transform]
 * @returns {Array}
 */
export const lift_first_fare_amenities = (traveler_pricings, transform) => {
  const pricings = Array.isArray(traveler_pricings) ? traveler_pricings : []
  const { fareDetailsBySegment = [] } = pricings[0] || {}
  const { amenities = [] } = fareDetailsBySegment[0] || {}
  const list = Array.isArray(amenities) ? amenities : []
  return transform ? list.map(transform) : list.slice()
}

/**
 * Build a canonical FlightItinerary tail (firstDeparture / lastArrival
 * / stopsCount) from already-built segments. Each provider supplies
 * its own id + duration extraction and hands them in plus the built
 * segments and a pre-resolved empty endpoint.
 *
 * @param {{ id: string, duration: string, segments: Array, empty_endpoint: Object }} params
 * @returns {Object}
 */
export const finalize_itinerary = ({ id, duration, segments, empty_endpoint }) => {
  const first = segments[0]
  const last = segments[segments.length - 1]
  return {
    id,
    duration,
    segments,
    firstDeparture: first ? first.departure : empty_endpoint,
    lastArrival: last ? last.arrival : empty_endpoint,
    stopsCount: Math.max(0, segments.length - 1),
  }
}

/**
 * Per-deck summary emitted alongside the seat grid. Shape is identical
 * across providers — totals, free/chargeable flags, optional
 * priceRange. Callers may attach extra provider-specific fields on the
 * deck after this returns (Amadeus adds hasBlockedSeats /
 * hasPremiumSeats on the deck itself, not on the summary).
 *
 * @returns {Object}
 */
export const build_deck_summary = ({
  totalSeats,
  availableSeats,
  hasFreeSeats,
  hasChargeableSeats,
  minPrice,
  maxPrice,
  currency,
  currencySymbol,
}) => ({
  totalSeats,
  availableSeats,
  occupancyRate: totalSeats > 0 ? (totalSeats - availableSeats) / totalSeats : 0,
  hasFreeSeats,
  hasChargeableSeats,
  priceRange: minPrice !== null && minPrice !== undefined
    ? {
        min: String(minPrice),
        max: String(maxPrice),
        currency,
        currencySymbol,
      }
    : null,
})

/**
 * Per-traveler seat-availability summary emitted alongside a deck.
 * Identical across providers — Amadeus and Kyte both produce this
 * shape via build_deck_seatmap. Inputs are the running per-traveler
 * maps populated during seat traversal.
 *
 * @returns {Object}
 */
export const build_passenger_summary = ({
  travelerIds,
  availableSeatsCount = {},
  availableSeatsRemoved = {},
  passengerHasInfantSeat = {},
  passengerMinPrice = {},
  passengerMaxPrice = {},
}) => {
  const summary = {}
  travelerIds.forEach((travelerId) => {
    summary[travelerId] = {
      availableSeatsCount: availableSeatsCount[travelerId] || 0,
      availableSeatsRemoved: availableSeatsRemoved[travelerId] || 0,
      hasInfantSuitableSeat: !!passengerHasInfantSeat[travelerId],
      cheapestSeatPrice: passengerMinPrice[travelerId] || null,
      mostExpensiveSeatPrice: passengerMaxPrice[travelerId] || null,
    }
  })
  return summary
}

export const collect_marketing_carriers = (itineraries) => {
  const seen = {}
  const list = Array.isArray(itineraries) ? itineraries : []
  list.forEach((itin) => {
    const segments = Array.isArray(itin.segments) ? itin.segments : []
    segments.forEach((seg) => {
      const { carrier: { code = '' } = {} } = seg || {}
      if (code) {
        seen[code] = true
      }
    })
  })
  return Object.keys(seen)
}
